// the-anthros-chair-2026.js - UI glue layer for the V2 Dopple configurator
// =============================================================================
// DoppleXR instantiation, configMenu, selection defaults, and SDK loading are
// in dopple-init.js (loaded as a module from <head>). That module sets
// window.dopple, window.configMenu, and fires dopple.run(). This file wires
// the UI and exposes the functions Dopple's UI expects on window.
// =============================================================================

window.updateSelection = updateSelection;
window.setCameraView = setCameraView;
window.setAutoRotation = setAutoRotation;
window.applyPreset = applyPreset;
window.clickUntilSelected = clickUntilSelected;
window.captureCartSnapshot = captureCartSnapshot;

// =============================================================================
// DOPPLE API FUNCTIONS
// =============================================================================

async function updateSelection(newSelection) {
    try {
        await window.dopple.updateSelection({
            'autoRotation': 'off',
            ...newSelection
        })
    } catch (error) {
        console.error('Error updating selection:', error);
    }
}

// Helper function to update the 3D camera to different preset views
function setCamera(view, smooth = true) {
    let position
    let target
    
    switch (view) {
        case 'upper-back-adjust':
        case 'upperbackAdjust':
            position = { x: 1.11, y: 0.38, z: -1.03 }
            target = { x: 0, y: 0.46, z: -0.04 }
            break
        case 'low-back-adjust':
        case 'lowbackAdjust':
            position = { x: -1.28, y: 0.18, z: -0.77 }
            target = { x: 0, y: 0.4, z: -0.04 }
            break
        case 'height-adjust':
        case 'heightAdjust':
            position = { x: -0.98, y: 0.23, z: -0.66 }
            target = { x: 0, y: 0.45, z: -0.04 }
            break
        case 'tilt-adjust':
        case 'tiltAdjust':
            position = { x: -0.67, y: -0.13, z: 1.12 }
            target = { x: 0, y: 0.45, z: -0.04 }
            break
        case 'back':
            position = { x: -1.1, y: 0.5, z: -2.82 }
            target = { x: 0, y: 0.5, z: -0.04 }
            break
        case 'front':
        default:
            position = { x: 1.28, y: 1, z: 2.61 }
            target = { x: 0, y: 0.5, z: -0.04 }
            break
    }
    
    window.dopple.controls.normalizeRotations().setPosition(position.x, position.y, position.z, smooth)
    window.dopple.controls.normalizeRotations().setTarget(target.x, target.y, target.z, smooth)
}

// Alias for backwards compatibility
async function setCameraView(view, smooth = true) {
    setCamera(view, smooth)
}

async function setAutoRotation(enabled) {
    await updateSelection({ 'autoRotation': enabled ? 'on' : 'off' })
}

async function applyPreset(presetName) {
    const presets = {
        'drlupo': {
            'chassisColor': 'onyx',
            'backPanel': 'drLupo',
            'cameraView': 'front'
        },
        'gaming': {
            'chassisColor': 'onyx',
            'cameraView': 'front'
        },
        'bitcoin': {
            'chassisColor': 'onyx',
            'backPanel': 'bitcoinLetterpress',
            'cameraView': 'front'
        },
        'miami_heretics': {
            'chassisColor': 'onyx',
            'backPanel': 'miamiHeretics',
            'cameraView': 'front'
        },
        'onyx-dunePort-walnut': {
            'fabric': 'dunePort',
            'backPanel': 'walnut'
        },
        'quartz-ivoryPort-circuitWhite': {
            'chassisColor': 'quartz',
            'fabric': 'ivoryPort',
            'backPanel': 'circuitIvory'
        },
        'onyx-ivoryAthlon-walnut': {
            'fabric': 'ivoryGAFabric',
            'backPanel': 'walnut'
        },
        'quartz-lavenderAthlon-circuitBlack': {
            'chassisColor': 'quartz',
            'fabric': 'lavenderGAFabric',
            'backPanel': 'circuitOnyx'
        },
        'bitcoin-onyx-ivoryAthlon-cream': {
            'fabric': 'ivoryGAFabric',
            'backPanel': 'bitcoinMoney'
        },
        'quartz-onyxPort-naturalOak': {
            'chassisColor': 'quartz',
            'fabric': 'onyxPort',
            'backPanel': 'naturalOak'
        },
        'onyx-lavenderAthlon-circuitBlack': {
            'fabric': 'lavenderGAFabric',
            'backPanel': 'circuitOnyx'
        },
        'quartz-steelAthlon-standardOnyx': {
            'chassisColor': 'quartz',
            'fabric': 'steelGAFabric',
            'backPanel': 'standardBlack'
        },
        'AMERICA': {
            'fabric': 'ivoryGAFabric',
            'chassisColor': 'quartz',
            'controlArmCapColor': 'glossStone',
            'backPanel': 'colorFlagUSA'
        }
    }

    if (presets[presetName]) {
        await updateSelection(presets[presetName])
    }
}

function clickUntilSelected(
    selector,
    {
        container = '.mw-option__value',
        selectedClass = 'mw-option__value--selected',
        interval = 100,
        maxTries = 20
    } = {}
) {
    let tries = 0;
    const timer = setInterval(() => {
        const $input = $(selector);
        const $wrapper = $input.closest(container);

        if (!$wrapper.length || $wrapper.find('input:checked').length > 0) {
            clearInterval(timer);
            return;
        }

        if (++tries >= maxTries) {
            clearInterval(timer);
            return;
        }

        $input[0].click();
    }, interval);
}

// =============================================================================
// CART SNAPSHOT CAPTURE
// =============================================================================
const NETLIFY_BASE_URL = 'https://staging.anthros.com';

// Store blob here after capture, before cart/add response comes back
let pendingSnapshotBlob = null;

async function captureCartSnapshot() {
    console.log('Starting snapshot capture...');

    // Save current camera position so we can restore it after snapshot
    const prevPosition = window.dopple.controls.getPosition();
    const prevTarget = window.dopple.controls.getTarget();

    await window.dopple.controls.setPosition(-1.1, 0.5, -2.82, false);
    await window.dopple.controls.setTarget(0, 0.5, -0.04, false);

    const canvas = document.querySelector('#dopple-container canvas');

    if (!canvas) {
        console.error('Canvas not found');
        return;
    }

    console.log('Canvas found:', canvas.width, 'x', canvas.height);

    return new Promise((resolve) => {
        setTimeout(() => {
            try {
                const OUT_WIDTH = 850;
                const OUT_HEIGHT = 1000;
                const offscreen = document.createElement('canvas');
                offscreen.width = OUT_WIDTH;
                offscreen.height = OUT_HEIGHT;
                const ctx = offscreen.getContext('2d');
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, OUT_WIDTH, OUT_HEIGHT);

                // Preserve source aspect ratio — scale to fit, center, letterbox on white
                const scale = Math.min(OUT_WIDTH / canvas.width, OUT_HEIGHT / canvas.height);
                const drawWidth = canvas.width * scale;
                const drawHeight = canvas.height * scale;
                const offsetX = (OUT_WIDTH - drawWidth) / 2;
                const offsetY = (OUT_HEIGHT - drawHeight) / 2;

                ctx.drawImage(canvas, offsetX, offsetY, drawWidth, drawHeight);

                offscreen.toBlob((blob) => {
                    console.log(`Snapshot captured (${Math.round(blob.size / 1024)} KB)`);
                    pendingSnapshotBlob = blob;

                    const reader = new FileReader();
                    reader.onloadend = () => {
                        try {
                            sessionStorage.setItem('anthros_cart_snapshot', reader.result);
                            console.log('✅ Snapshot saved to sessionStorage');
                        } catch (storageError) {
                            console.error('❌ sessionStorage error:', storageError);
                        }
                    };
                    reader.readAsDataURL(blob);

                    window.dopple.controls.setPosition(prevPosition.x, prevPosition.y, prevPosition.z, false);
                    window.dopple.controls.setTarget(prevTarget.x, prevTarget.y, prevTarget.z, false);

                    resolve();
                }, 'image/jpeg', 0.85);
            } catch (err) {
                console.error('Canvas capture error:', err);
                resolve();
            }
        }, 100);
    });
}

async function uploadSnapshotForVariant(variantId, productId) {
    if (!pendingSnapshotBlob) {
        console.warn('No pending snapshot blob to upload');
        return;
    }

    try {
        // Read Klaviyo profile ID from cookie
        let klaviyoCid = null;
        try {
            const klaCookie = document.cookie.split(';').find(c => c.trim().startsWith('__kla_id'));
            if (klaCookie) {
                const decoded = JSON.parse(atob(klaCookie.split('=')[1]));
                if (decoded.$exchange_id) {
                    klaviyoCid = decoded.$exchange_id;
                    console.log('📧 Klaviyo exchange_id found:', klaviyoCid);
                }
            }
        } catch (e) {
            console.warn('Could not read Klaviyo cookie:', e);
        }

        console.log('📸 Uploading snapshot to Shopify...');
        const formData = new FormData();
        formData.append('image', pendingSnapshotBlob, 'chair-snapshot.jpg');
        formData.append('variant_id', variantId);
        if (klaviyoCid) {
            formData.append('klaviyo_cid', klaviyoCid);
        }

        const uploadRes = await fetch(
            `${NETLIFY_BASE_URL}/api/upload-variant-image`,
            { method: 'POST', body: formData }
        );
        const uploadData = await uploadRes.json();

        if (uploadData.success) {
            console.log('✅ Variant image uploaded:', uploadData.image_url);
        } else {
            console.error('❌ Upload failed:', uploadData.error);
        }
    } catch (err) {
        console.error('❌ Upload error:', err);
    } finally {
        pendingSnapshotBlob = null;
        hideBuildingOverlay();
    }
}

// Poll cart.js after add to get the real MageWorx variant ID
const uploadedVariants = new Set();

async function getLatestCartVariant() {
    try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        if (cart.items && cart.items.length > 0) {
            // Find the first item we haven't uploaded for yet
            const newItem = cart.items.find(i => 
                i.product_type === 'mws_apo_generated' && 
                !uploadedVariants.has(i.variant_id)
            );
            if (newItem) {
                console.log('🛒 Latest cart item - variant:', newItem.variant_id, 'product:', newItem.product_id);
                return { variantId: newItem.variant_id, productId: newItem.product_id };
            }
        }
    } catch (e) {
        console.warn('Could not fetch cart:', e);
    }
    return null;
}

// =============================================================================
// BUILDING YOUR CHAIR OVERLAY
// =============================================================================
function showBuildingOverlay() {
    const existing = document.getElementById('anthros-building-overlay');
    if (existing) return;

    const overlay = document.createElement('div');
    overlay.id = 'anthros-building-overlay';
    overlay.innerHTML = `
        <div class="anthros-building-inner">
            <div class="anthros-building-spinner"></div>
            <div class="anthros-building-text">BUILDING YOUR CHAIR</div>
            <div class="anthros-building-sub">Saving your custom configuration...</div>
        </div>
    `;

    const style = document.createElement('style');
    style.id = 'anthros-building-style';
    style.textContent = `
        #anthros-building-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.72);
            z-index: 2147483647;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: anthrosFadeIn 0.3s ease;
        }
        @keyframes anthrosFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        @keyframes anthrosFadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
        }
        .anthros-building-inner {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
            padding: 48px 56px;
            background: #fff;
            border-radius: 12px;
            box-shadow: 0 24px 64px rgba(0,0,0,0.3);
            text-align: center;
            max-width: 360px;
        }
        .anthros-building-spinner {
            width: 48px;
            height: 48px;
            border: 4px solid #e5e5e5;
            border-top-color: #1a1a1a;
            border-radius: 50%;
            animation: anthrosSpin 0.8s linear infinite;
        }
        @keyframes anthrosSpin {
            to { transform: rotate(360deg); }
        }
        .anthros-building-text {
            font-family: sans-serif;
            font-size: 18px;
            font-weight: 700;
            letter-spacing: 0.08em;
            color: #1a1a1a;
        }
        .anthros-building-sub {
            font-family: sans-serif;
            font-size: 13px;
            color: #888;
            letter-spacing: 0.02em;
        }
    `;

    document.head.appendChild(style);
    document.body.appendChild(overlay);

    // Disable slide cart checkout button
    const checkoutBtn = document.querySelector('#slidecarthq a[href="/checkout"], #slidecarthq button[name="checkout"]');
    if (checkoutBtn) {
        checkoutBtn.dataset.anthrosDisabled = 'true';
        checkoutBtn.style.pointerEvents = 'none';
        checkoutBtn.style.opacity = '0.4';
    }
}

function hideBuildingOverlay() {
    const overlay = document.getElementById('anthros-building-overlay');
    if (!overlay) return;
    overlay.style.animation = 'anthrosFadeOut 0.3s ease forwards';
    // Re-enable slide cart checkout button
    const checkoutBtn = document.querySelector('[data-anthros-disabled="true"]');
    if (checkoutBtn) {
        checkoutBtn.removeAttribute('data-anthros-disabled');
        checkoutBtn.style.pointerEvents = '';
        checkoutBtn.style.opacity = '';
    }
    setTimeout(() => {
        overlay.remove();
        document.getElementById('anthros-building-style')?.remove();
    }, 300);
}

// =============================================================================
// ADD TO CART SNAPSHOT INTEGRATION
// =============================================================================
document.addEventListener('click', async function(e) {
    if (e.target.closest('.mw-apo-add-to-cart-cloned')) {

        console.log('Add to cart clicked');

        // Capture snapshot before cart add fires
        await captureCartSnapshot();
        showBuildingOverlay();

        // Poll cart until MageWorx variant appears, then upload
        let pollAttempts = 0;
        const maxAttempts = 20; // 10 seconds max
        const cartPoll = setInterval(async () => {
            pollAttempts++;
            const cartItem = await getLatestCartVariant();
            if (cartItem) {
                clearInterval(cartPoll);
                uploadedVariants.add(cartItem.variantId);
                console.log('🛒 Cart item found after', pollAttempts * 500, 'ms');
                await uploadSnapshotForVariant(cartItem.variantId, cartItem.productId);
            } else if (pollAttempts >= maxAttempts) {
                clearInterval(cartPoll);
                hideBuildingOverlay();
                console.warn('❌ Cart item never appeared after 10s');
            }
        }, 500);

        // Inject snapshot repeatedly for 15 seconds
        let injectionCount = 0;
        const pollInterval = 100;
        const duration = 15000;
        const startTime = Date.now();

        const pollAndInject = setInterval(() => {
            const elapsed = Date.now() - startTime;

            if (elapsed > duration) {
                clearInterval(pollAndInject);
                console.log(`Stopped - ${injectionCount} total injections over ${Math.round(elapsed/1000)}s`);
                sessionStorage.removeItem('anthros_cart_snapshot');
                sessionStorage.removeItem('anthros_cart_config');
                return;
            }

            const snapshot = sessionStorage.getItem('anthros_cart_snapshot');
            if (!snapshot) return;

            // Target img or span placeholder
            const cartImage = document.querySelector(
                '#slidecarthq img[alt="Anthros Chair Dopple V2"], #slidecarthq img[alt="Anthros Chair"]'
            );
            const cartPlaceholder = document.querySelector(
                '#slidecarthq .image-placeholder, #slidecarthq .item-image-anchor span'
            );

            const imageStyles = 'width:100px !important;height:100px !important;min-height:100px !important;max-height:100px !important;object-fit:cover !important;object-position:center !important;display:block !important;aspect-ratio:1/1 !important;flex-shrink:0 !important;';
            const containerStyles = 'width:100px !important;height:100px !important;min-height:100px !important;max-height:100px !important;overflow:hidden !important;flex-shrink:0 !important;display:flex !important;align-items:center !important;';

            if (cartImage && !cartImage.src.startsWith('data:')) {
                injectionCount++;
                cartImage.src = snapshot;
                cartImage.removeAttribute('srcset');
                cartImage.style.cssText = imageStyles;
                cartImage.closest('.item-image-anchor')?.setAttribute('style', containerStyles);

            } else if (cartPlaceholder) {
                injectionCount++;
                const img = document.createElement('img');
                img.src = snapshot;
                img.alt = 'Anthros Chair';
                img.style.cssText = imageStyles;
                cartPlaceholder.closest('.item-image-anchor')?.setAttribute('style', containerStyles);
                cartPlaceholder.replaceWith(img);
            }
        }, pollInterval);
    }
}, true);

// =============================================================================
// RADIO BUTTON INTEGRATION WITH DOPPLE
// =============================================================================

let optionsHandled = false;
function handleOptionsReady() {
    if (optionsHandled) return;
    optionsHandled = true;
    try {
        const radioInputs = document.querySelectorAll('input[type="radio"]');

        radioInputs.forEach(radio => {
            radio.addEventListener('input', function() {
                try {
                    const parentLabelEl = radio.parentElement;
                    const labelText = parentLabelEl.querySelector('.title_name')?.textContent;
                    
                    if (!labelText) return;

                    const optionContainerEl = radio.closest('.mw-option');
                    if (!optionContainerEl) return;

                    const titleEl = optionContainerEl.querySelector('.title');
                    if (!titleEl) return;

                    const title = titleEl.textContent;
                    const configItem = configMenu.find(option => option.title === title);
                    
                    if (configItem) {
                        const option = configItem.options.find(opt => 
                            opt.title.toLowerCase() === labelText.toLowerCase()
                        );
                        
                        if (option) {
                            updateSelection({
                                [configItem.property]: option.value
                            });
                        }
                    }
                } catch (error) {
                    console.error('Error handling radio input:', error);
                }
            });
        });
    } catch (error) {
        console.error('Error setting up option handlers:', error);
    }
}

window.addEventListener('optionsReady', handleOptionsReady);
setTimeout(handleOptionsReady, 4000);

// =============================================================================
// GSAP LOADING ANIMATIONS
// =============================================================================

var _st1 = 0;
$(window).scroll(function() {
    var _n = Date.now(); if (_n - _st1 < 100) return; _st1 = _n;
    if ($(this).scrollTop() > 300) {
        $('.stock-top').css('opacity', 0);
    } else {
        $('.stock-top').css('opacity', 1);
    }
});

// Loading animation is handled by CSS in main-the-anthros-chair-2026.liquid.
// created 50+ repeat:-1 tweens competing with the WebGL rAF loop, and contributed
// ~1.3 s of JS execution blocking the main thread at startup.

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

function show_hide_options(es) {
    $(es).toggleClass('activemore');
    $('.o_2184551, .o_2184553, .o_2184554, .o_2184555, .o_2184556, .o_2184557, .o_2206346').toggleClass('optionhidden');

    if ($(es).hasClass('activemore')) {
        $(es).text('LESS CUSTOMIZATION');
    } else {
        $(es).text('MORE CUSTOMIZATION');
    }
}

$(".gallary-bg").click(function() {
    $(this).addClass("animated fadeOut");
    $(this).children().children().addClass("fadeLeft");
    $(this).children().remove();
    var rmBg = $(this);
    setTimeout(() => {
        rmBg.removeClass("gallary-bg-block");
    }, 10);
});

const closemodal = img => {
    $(img).addClass("animated fadeOut");
    $(img).children().children().addClass("fadeLeft");
    $(img).children().remove();
    var rmBg = $(img);
    setTimeout(() => {
        rmBg.removeClass("gallary-bg-block");
    }, 400);
}

const openModal = img => {
    const imgClone = $(img).closest('.mw-stack__item').find('input[type="radio"]').attr('data-image');
    $(".gallary-bg").addClass("gallary-bg-block")
    $(".gallary-bg img").attr('onclick', '');
    $(".gallary-bg img").addClass("animated fadeIn");
    $(".gallary-bg").removeClass("animated fadeOut");
    $('.gallary-bg img').attr('id', 'myimage');
}

function numberWithCommas(number) {
    var parts = number.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
}

// Set to false after sale to revert all promo behavior in one change
const MEMORIAL_DAY_ACTIVE = false;
// One deadline for both the desktop countdown and the mobile carousel's.
const SALE_DEADLINE = '2026-10-19T06:00:00-05:00';

const MEMORIAL_ORIGINAL_PRICES = {
  'v_18919682': 99,   // Quartz chair color
  'v_18919694': 79,   // Natural Oak back
  'v_20901620': 79,   // Amber Elm back
  'v_19580847': 79,   // Ivory Ash back
  'v_19580848': 79,   // Greige Oak back
  'v_25609381': 79,   // Warm Ebony back
  'v_18919695': 149,  // Walnut back
  'v_18919696': 299,  // Carbon Fiber back
  'v_18919697': 79,   // Circuit Black back
  'v_18919698': 79,   // Circuit White back
  'v_19939682': 79,   // Skeleton White back
  'v_19939683': 79,   // Skeleton Black back
  'v_20110214': 79,   // Heretics back
  'v_26494567': 79,   // Evil Geniuses back
  'v_20616603': 79,   // US Flag back
  'v_19580996': 79,   // Bitcoin Money back
  'v_19580997': 79,   // Bitcoin Letterpress back
  'v_18919689': 199,  // Ivory Port fabric
  'v_18919690': 199,  // Onyx Port fabric
  'v_18919691': 199,  // Steel Port fabric
  'v_18919692': 199,  // Dune Port fabric
  'v_18919706': 39,   // Shiny Chrome caps
  'v_18919707': 49,   // Shiny Gold caps
};

function getMemorialSavings() {
    let savings = 0;
    document.querySelectorAll('.mw-apo-container input[type="radio"]:checked').forEach(input => {
        if (MEMORIAL_ORIGINAL_PRICES[input.id]) {
            savings += MEMORIAL_ORIGINAL_PRICES[input.id];
        }
    });
    return savings;
}


// ─── Sale Config (read by the mobile carousel) ───────────────────────────
// Desktop (1025px+) renders the sale through the legacy path above:
// MEMORIAL_DAY_ACTIVE, injectMemorialSavingsLines, initMemorialStrikethrough and
// initMemorialCountdown. Mobile renders it through configurator-carousel-2026.js,
// which reads this object. Both share MEMORIAL_ORIGINAL_PRICES, so the numbers
// can't drift between the two layouts.
//
// PREREQUISITE: the upcharges for the included options must actually be set to
// $0 in MageWorx APO. The struck price is the live APO price plus the upcharges
// listed in MEMORIAL_ORIGINAL_PRICES, so if APO is still charging for an option
// that sits in that map, the strike would overstate the discount.
window.ANTHROS_SALE = {
    active: MEMORIAL_DAY_ACTIVE,
    label: 'WORLD SPINE DAY DISCOUNT',
    deadline: SALE_DEADLINE,
    maxSavings: 646,
    bannerDesktop: 'https://cdn.shopify.com/s/files/1/0611/7552/9642/files/world_spine_day_desktop_banner.png?v=1791897365',
    bannerMobile: 'https://cdn.shopify.com/s/files/1/0611/7552/9642/files/world_spine_day_mobile_banner.png?v=1791897365',
    bannerAlt: 'Free options upgrades. Save with our World Spine Day deal.',
    originalPrices: MEMORIAL_ORIGINAL_PRICES,
    getSavings: getMemorialSavings
};

function formatPrice(amount) {
  return '$' + amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function injectMemorialSavingsLines() {
    document.querySelectorAll('.price__container').forEach(container => {
        // Don't double inject
        if (container.parentElement.querySelector('.memorial-savings-line')) return;

        const savingsLine = document.createElement('div');
        savingsLine.className = 'memorial-savings-line';
        savingsLine.style.display = 'none';
        savingsLine.innerHTML = '<s class="memorial-strike-price"></s>';

        // Insert right after price__container, before the badges
        container.insertAdjacentElement('afterend', savingsLine);
    });
}

function updateMemorialStrikethrough() {
    const savings = getMemorialSavings();

    document.querySelectorAll('.memorial-savings-line').forEach(el => {
        if (savings === 0) {
            el.style.display = 'none';
            return;
        }

        const priceEl = document.querySelector('.price__regular .price-item--regular');
        if (!priceEl) return;

        const actualPrice = parseFloat(priceEl.textContent.replace(/[$,]/g, ''));
        if (isNaN(actualPrice)) return;

        const strikeEl = el.querySelector('.memorial-strike-price');
        if (strikeEl) strikeEl.textContent = formatPrice(actualPrice + savings);

        el.style.display = '';
    });
}

// =============================================================================
// MAGEWORX APO SETUP & PRICE CALCULATIONS
// =============================================================================

$(document).ready(function() {
    $('.price-item--regular').attr('style', 'opacity:0');
    
    setTimeout(function() {
        $('#custom_add_to_cart_btn').click(function(e) {
            var isvalid = true;

            $('div[data-helptext-type="required"]').each(function() {
                var text = $(this).text();
                var select_val = $(this).closest('.mw-option--required').find('select').val();
                
                if (select_val != undefined && select_val != 'undefined') {
                    if (select_val == '') {
                        $('#variant_error').remove();
                        $('.mw-apo-container').append('<p id="variant_error" style="color:red;">' + text + '</p>');
                        isvalid = false;
                        return false;
                    } else {
                        $('#variant_error').remove();
                    }
                } else {
                    if ($(this).closest('.mw-option--required').find('input[type="radio"]:checked').length < 1) {
                        $('#variant_error').remove();
                        $('.mw-apo-container').append('<p id="variant_error" style="color:red;">' + text + '</p>');
                        isvalid = false;
                        return false;
                    } else {
                        $('#variant_error').remove();
                    }
                }
            })
        })
    }, 5000);

    setTimeout(function() {
        $('.mw-label').hover(function() {
            var img = $(this).find('input[type="radio"]').attr('data-image');
            
            setTimeout(function() {
                if (img != null && img != undefined && img != 'undefined') {
                    $(".gallary-bg").html('<div class="img_center"><img src="' + img + '" /></div>');
                }
            }, 200);
        })
        
        setTimeout(function() {
            $('.chair_summary').each(function() {
                // Setup complete
            })
            
            $('.tab').click(function() {
                if ($(this).hasClass('active')) {
                    if ($(this).attr('data-id') == 'o_520080') {
                        $('#o_520087').hide();
                    }
                    if ($(this).attr('data-id') == 'o_2081593') {
                        $('#o_2081594').hide();
                        $('#o_520257').hide();
                    }
                    if ($(this).attr('data-id') == 'o_502386') {
                        $('#o_525895').hide();
                    } 
                    
                    $('.tabcontent').hide(400);
                    $('.tab').removeClass('active');
                } else {
                    if ($(this).attr('data-id') == 'o_520080') {
                        $('#o_520087').show();
                    } else if ($(this).attr('data-id') != undefined) {
                        $('#o_520087').hide();
                    }
                    
                    if ($(this).attr('data-id') == 'o_2081593') {
                        $('#o_2081594').show();
                        $('#o_520257').show();
                        $('#o_638388').show();
                    } else if ($(this).attr('data-id') != undefined) {
                        $('#o_2081594').hide();
                    }
                    
                    if ($(this).attr('data-id') == 'o_502386') {
                        $('#o_525895').show();
                    } else if ($(this).attr('data-id') != undefined) {
                        $('#o_525895').hide();
                    }

                    if ($(this).attr('data-id') == 'o_638387') {
                        // Handle specific case
                    } else if ($(this).attr('data-id') != undefined) {
                        // Handle default
                    }

                    $('.tabcontent').hide();
                    $(this).closest('.chair_summary').find('.tabcontent').first().show(400);
                    $('.tab').removeClass('active');
                    $(this).toggleClass('active');
                }
                
                if ($(window).width() < 993) {
                    $("html, body").animate({
                        scrollTop: $(this).offset().top - 435
                    }, 300); 
                }
            })
        }, 2000);

        // Update section titles for fabric and back panel
        function updateSectionTitles() {
            $('.o_2184551 .shapeknit_text h2 .new_title_name, .o_2184551 .port_leather_text h2 .new_title_name').remove();
            $('.o_2184552 .standard_black_text h2 .new_title_name, .o_2184552 .wood_text h2 .new_title_name, .o_2184552 .specialty_text h2 .new_title_name').remove();

            function getStrike(valueId) {
                if (!MEMORIAL_DAY_ACTIVE) return '';
                const orig = MEMORIAL_ORIGINAL_PRICES[valueId];
                return orig ? '<del>$' + orig + '</del> ' : '';
            }

            // For o_2184551 (Fabric)
            $('.o_2184551 input[type="radio"]:checked').each(function() {
                let valueId = $(this).val();
                let title_name = $(this).closest('div').find('.title_name').text();
                let title_format = title_name.split('+');
                let bold_title = title_format[1] != undefined ? '+' + title_format[1] : '';
                let strike = getStrike(valueId);

                let parentSection = $(this).closest('.mw-option__value').parent();
                let targetH2 = null;

                if (parentSection.hasClass('shapeknit')) {
                    targetH2 = $('.o_2184551 .shapeknit_text h2');
                } else if (parentSection.hasClass('port_leather')) {
                    targetH2 = $('.o_2184551 .port_leather_text h2');
                }

                if (targetH2 && targetH2.length) {
                    targetH2.append('<span class="new_title_name"> ' + title_format[0] + strike + '<b>' + bold_title + '</b></span>');
                }
            });

            // For o_2184552 (Back Panel)
            $('.o_2184552 input[type="radio"]:checked').each(function() {
                let valueId = $(this).val();
                let title_name = $(this).closest('div').find('.title_name').text();
                let title_format = title_name.split('+');
                let bold_title = title_format[1] != undefined ? '+' + title_format[1] : '';
                let strike = getStrike(valueId);

                let parentSection = $(this).closest('.mw-option__value').parent();
                let targetH2 = null;

                if (parentSection.hasClass('standard_black')) {
                    targetH2 = $('.o_2184552 .standard_black_text h2');
                } else if (parentSection.hasClass('wood_section')) {
                    targetH2 = $('.o_2184552 .wood_text h2');
                } else if (parentSection.hasClass('specialty') || parentSection.hasClass('bitcoin_data')) {
                    targetH2 = $('.o_2184552 .specialty_text h2');
                }

                if (targetH2 && targetH2.length) {
                    targetH2.append('<span class="new_title_name"> ' + title_format[0] + strike + '<b>' + bold_title + '</b></span>');
                }
            });
        }

        // Price calculation function
        function change_image(et) {
            var rules = localStorage.getItem('rules');
            var variant_name = $(et).attr('name');
            var isvalid = true;

            $('div[data-helptext-type="required"]').each(function() {
                var text = $(this).text();
                var select_val = $(this).closest('.mw-option--required').find('select').val();
                
                if (select_val != undefined && select_val != 'undefined') {
                    if (select_val == '') {
                        isvalid = false;
                    } else {
                        $('#variant_error').remove();
                    }
                } else {
                    if ($(this).closest('.mw-option--required').find('input[type="radio"]:checked').length < 1) {
                        isvalid = false;
                    } else {
                        $('#variant_error').remove();
                    }
                }
            })
        
            if (isvalid == true) {
                $('#custom_add_to_cart_btn').hide();
                $('.mw-apo-add-to-cart-cloned').show();
            } else {
                $('#custom_add_to_cart_btn').hide();
                $('.mw-apo-add-to-cart-cloned').show();
            }

            var pr_price = $('#product_price').val();
            pr_price = parseFloat(pr_price);

            var variant_price = 0;
            $('.product-form input[type="radio"]:checked').each(function() {
                variant_price = variant_price + parseFloat($(this).attr('data-price'));
            })
            
            $('.product-form select option:selected').each(function() {
                var prc = $(this).attr('data-price');
                if (prc != undefined && prc != 'undefined' && prc != null && prc != '') {
                    variant_price = variant_price + parseFloat($(this).attr('data-price'));
                }
            })
            
            var total_price = pr_price + variant_price;
            
            if (!isNaN(total_price)) {
                var extra_price = total_price + 345
                $('.price__regular .price-item--regular').text('$' + numberWithCommas(total_price.toFixed(2)) + '');;
                $('.extra_price').text('$' + numberWithCommas(extra_price.toFixed(2)));
            }
            
            $('.klarna_price').text('$' + (total_price / 4).toFixed(2));
            variant_price = parseFloat(variant_price);

            $.each(JSON.parse(rules), function(index, value) {
                var is_true = true;
                var str1 = value.rule.title;
                if (value.rule.title != '' && str1.indexOf('{index}') != -1) {
                    $.each(value.rule.conditions, function(ind, val) {
                        var option_val = $('input[name="mw_opts[' + val.option_id + ']"]:checked').val();
                    
                        if (option_val == 'v_502382_l6dixwf8') {
                            $('.4-stroke-soft').show();
                        } else if (option_val == 'v_2883688') {
                            $('.4-stroke-soft').hide();
                        }
                        
                        if ($.inArray(option_val, val.values) == -1) {
                            is_true = false;
                        }
                    })
                } else {
                    is_true = false;
                }
                if (is_true == true) {
                    return value.rule.title;
                }
            })
        }
        
        setTimeout(function() {
            $('.product-form input[type="radio"], .product-form select').each(function() {
                var name = change_image($(this));
            })
            if (MEMORIAL_DAY_ACTIVE) setTimeout(updateMemorialStrikethrough, 200);
            
            $('.product-form input[type="radio"]:checked').each(function() {
                let title_name = $(this).closest('div').find('.title_name').text();
                let title_format = title_name.split('+');
                let bold_title = '';
                if (title_format[1] != undefined) {
                    bold_title = '+' + title_format[1];
                }
                const orig = MEMORIAL_DAY_ACTIVE ? MEMORIAL_ORIGINAL_PRICES[$(this).val()] : null;
                const strikeHtml = orig ? '<del style="color:#999;font-weight:normal;">$' + orig + '</del> ' : '';

                $(this).closest('.chair_summary').find('.new_title_name').remove();
                $(this).closest('.chair_summary').find('h4').append('<span class="new_title_name">' + title_format[0] + strikeHtml + '<b>' + bold_title + '</b>' + '</span>');
                $(this).closest('.mw-option__value').addClass('radio_active');
            })
            
            setTimeout(function() {
                updateSectionTitles();
            }, 500);
            
            $('.product-form input[type="radio"]').change(function() {
                let title_name = $(this).closest('div').find('.title_name').text();
                let title_format = title_name.split('+');
                let bold_title = '';
                if (title_format[1] != undefined) {
                    bold_title = '+' + title_format[1];
                }
                const orig = MEMORIAL_DAY_ACTIVE ? MEMORIAL_ORIGINAL_PRICES[$(this).val()] : null;
                const strikeHtml = orig ? '<del style="color:#999;font-weight:normal;">$' + orig + '</del> ' : '';

                $(this).closest('.chair_summary').find('.new_title_name').remove();
                $(this).closest('.chair_summary').find('h4').append('<span class="new_title_name">' + title_format[0] + strikeHtml + '<b>' + bold_title + '</b>' + '</span>');
                $(this).closest('.mw-option__container').find('.mw-option__value').removeClass('radio_active');
                $(this).closest('.mw-option__value').addClass('radio_active');
            })
            
            $('.product-form input[type="radio"], .product-form select').change(function() {
                var valchecked = $(this).val();
                
                $(this).closest('.mw-option__container').find('.mw-option__value').removeClass('radio_active');
                $(this).closest('.mw-option__value').addClass('radio_active');
                
                if (valchecked == 'v_18929376') {
                    $('body').addClass('darkmode');
                }
                if (valchecked == 'v_18929375') {
                    $('body').removeClass('darkmode');
                }
            
                var img_click = $(this).attr('data-image');
                if (img_click != '' && img_click != undefined && img_click != 'undefined' && img_click != null) {
                    // Handle image click
                }
                
                if (valchecked == 'v_17990463') {
                    $('#v_17990486').click();
                }
                if (valchecked == 'v_17990464') {
                    $('#v_17990487').click();
                }
                if (valchecked == "v_3035424" || valchecked == "v_3035425" || valchecked == "v_520087_lhbj3vep") {
                    $('.showhide_weight').show();
                } else {
                    $('.showhide_weight').hide();
                }
                
                var name = change_image($(this));
                if (MEMORIAL_DAY_ACTIVE) setTimeout(updateMemorialStrikethrough, 200);
                
                if ($(this).is(':radio') && $(this).closest('.o_2184551, .o_2184552').length) {
                    updateSectionTitles();
                }
            })
        }, 1000);
    }, 3000);

    var _st2 = 0;
    $(window).scroll(function() {
        var _n = Date.now(); if (_n - _st2 < 100) return; _st2 = _n;
        var sticky = $('.header-wrapper'),
            scroll = $(window).scrollTop();

        if (scroll >= 50) sticky.addClass('added_class');
        else sticky.removeClass('added_class');
    });

    var cache_hash = window.mwApoInit.cache_hash;
    var product_id = window.mwApoInit.product.id;
    var variants_id = window.mwApoInit.product.variants[0].id;

    // MageWorx APO Data Loading
    let checkExist = setInterval(function() {
        if ($('.mw-apo-container').length) {
            $.getJSON('https://apo-api.mageworx.com/front/product/anthroschair.myshopify.com/' + cache_hash + '/' + product_id + '/' + variants_id + '.js', function(jd) {

                localStorage.setItem('rules', JSON.stringify(jd.rules));
                var options = jd.options;
                var values = jd.values;

                setTimeout(function() {
                    $.each(values, function(key, val) {
                        if (val.value.input_type == 'select') {
                            $('select option[value="' + val.value.id + '"]').attr('data-price', val.value.pricing.price);
                        }
                        $('#' + val.value.id).attr('data-price', val.value.pricing.price);
                        $('#' + val.value.id).closest('.mw-stack__item').append('<span class="title_name">' + val.value.title + '</span>');
                        
                        if (val.value.id == "v_3993314" || val.value.id == "v_3993315") {
                            $('#' + val.value.id).closest('.mw-stack__item').append(val.value.description);
                        }
                        
                        if (val.value.images[0] != null && val.value.images[0] != undefined) {
                            var hover_image = val.value.images[0]['url'];
                            $('#' + val.value.id).attr('data-image', val.value.images[0]['url']); 
                            var disc_class = '';
                            var option_description = '';
                            if (val.value.description != '') {
                                disc_class = 'with_description';
                                option_description = '<div class="option_description">' + val.value.description + '</div>';
                            }
                        }
                    })
                    
                    var event = new Event("optionsReady");
                    window.dispatchEvent(event);
                }, 1000);

                $.each(options, function(index, value) {
                    setTimeout(function() {
                        $('#' + value.id).wrap('<div class="chair_summary ' + value.id + '"></div>');
                        
                        if (value.id != 'o_2081594' && value.id != 'o_2080843' && value.id != 'o_520257' && value.id != 'o_525895' && value.id != 'o_638388') {
                            if (value.id == "o_2081589") {
                                $('<h4 class="tab1 active" data-id="' + value.id + '" >' + value.title + '</h4>').insertBefore($('#' + value.id)); 
                                $('#' + value.id).show();
                            } else {
                                $('<h4 class="tab1" data-id="' + value.id + '" >' + value.title + '</h4>').insertBefore($('#' + value.id)); 
                            }
                        }
                        
                        $('#' + value.id).find('.mw-text--style-strong').html(value.name);
                        $('#' + value.id).append(value.description);
                    }, 100);

                    if (value.id == "o_2184551") {
                        setTimeout(function() {
                            $('[data-value-id="v_18919683"], [data-value-id="v_18919684"], [data-value-id="v_18919685"], [data-value-id="v_18919686"], [data-value-id="v_18919687"], [data-value-id="v_18919688"]').wrapAll('<div class="shapeknit"/>'); 
                            $('div.mw-option__value[data-value-id="v_18919689"], div.mw-option__value[data-value-id="v_18919690"], div.mw-option__value[data-value-id="v_18919691"], div.mw-option__value[data-value-id="v_18919692"]').wrapAll('<div class="port_leather"/>'); 
                        }, 1000);
                    }
                    
                    if (value.id == "o_2184552") {
                        $('[data-value-id="v_18919693"],[data-value-id="v_25609380"] ').wrapAll('<div class="standard_black back_items"/>'); 
                        $('[data-value-id="v_18919694"], [data-value-id="v_19580847"], [data-value-id="v_18919695"], [data-value-id="v_19580848"], [data-value-id="v_19572775"], [data-value-id="v_19580849"], [data-value-id="v_19399710"], [data-value-id="v_20901620"], [data-value-id="v_25609381"]').wrapAll('<div class="wood_section back_items"/>'); 
                        $('[data-value-id="v_18919696"], [data-value-id="v_18919697"], [data-value-id="v_18919698"], [data-value-id="v_19371518"], [data-value-id="v_19371519"], [data-value-id="v_18919699"], [data-value-id="v_19371561"], [data-value-id="v_18919702"], [data-value-id="v_18919703"], [data-value-id="v_19354919"], [data-value-id="v_19354920"], [data-value-id="v_18919700"], [data-value-id="v_18919701"], [data-value-id="v_19939682"], [data-value-id="v_19939683"], [data-value-id="v_20110214"], [data-value-id="v_20616603"], [data-value-id="v_26494567"]').wrapAll('<div class="specialty back_items"/>'); 
                        $('[data-value-id="v_19580997"],[data-value-id="v_19580996"]').wrapAll('<div class="bitcoin_data"/>'); 
                    }
                });

                $('.list_items .item').click(function() {
                    var show_id = $(this).attr('data-id');
                    $('.mw-option--type-swatch').hide();
                    $('#' + show_id).show();
                    $('.list_items .item').removeClass('active');
                    $(this).addClass('active');
                })
            });
            
            $('<div class="shapeknit_text"><h2>Athlon Performance Fabric</h2><p>A high performance upholstery designed to meet the demands of daily use while providing exceptional comfort and style.</p></div>').insertBefore($('div[data-value-id="v_18919683"]'));
            $('<div class="port_leather_text"><h2>Port Leather Alternative</h2><p>An environmentally conscious synthetic leather that is phthalate free. Wipeable for cleaning and disinfecting</p></div>').insertBefore($('div[data-value-id="v_18919689"]'));
            $('<div class="standard_black_text back_items"><h2>Standard</h2></div>').insertBefore($('div[data-value-id="v_18919693"]'));
            $('<div class="wood_text back_items"><h2 style="display: inline; margin-right: 3px;">Wood Grain</h2><sup id="backs_infocon" style="display: inline;">&#9432;</sup><span id="backs_info" style="display: none;">Crafted with a refined wood aesthetic that captures the organic beauty and texture of natural wood grain.</span></div>').insertBefore($('div[data-value-id="v_18919694"]'));
            $('<div class="specialty_text back_items"><h2>Specialty</h2></div>').insertBefore($('div[data-value-id="v_18919696"]'));
            
            $('#backs_infocon').click(function() {
                $('#backs_info').toggle();
            });
            
            $('#backs_infocon').hover(
                function() {
                    $('#backs_info').show();
                }, 
                function() {
                    $('#backs_info').hide();
                }
            );
            
            $('.extra_infocon_1').hover(
                function() {
                    $('.extra_info').show();
                }, 
                function() {
                    $('.extra_info').hide();
                }
            );
            
            if (window.location.search.includes('gaming')) {
                if ($('#v_18929376').length > 0) {
                    $('#v_18929376')[0].click();
                    $('body').addClass('darkmode');
                }
            }
            
            if (window.location.search.includes('EFG-team')) {
                if ($('#v_18929376').length > 0) {
                    $('#v_18929376')[0].click();
                    $('body').addClass('darkmode');
                }
            }
            
            if (window.location.search.includes('bitcoin')) {
                setTimeout(function () {
                    $('#v_18929376').click();
                    $('body').addClass('darkmode');
                    $('[data-value-id="v_19580996"], [data-value-id="v_19580997"]').show();
                    $('.o_2185385, .o_2185372, .standard_black_text, .standard_black, .wood_text, .wood_section, .specialty_text, .specialty').hide();
                    $('.o_2184551, .o_2184553, .o_2184554, .o_2184555, .o_2184556, .o_2184557, .o_2206346').show();
                    $('.text_div p').html('CUSTOM BUILT &amp; SHIPPED IN <strong>7-10 BUSINESS DAYS</strong>');
                }, 500);
            }
            
            $('.product-form input[type="radio"]').change(function() {
                let title_name = $(this).closest('div').find('.title_name').text();
                if ($(this).val() == 'v_19129218') {
                    title_name += '<del>$199</del>'
                }
                let title_format = title_name.split('+');
                let bold_title = '';
                if (title_format[1] != undefined) {
                    bold_title = '+' + title_format[1];
                }
                $(this).closest('.chair_summary').find('.new_title_name').remove();
                $(this).closest('.chair_summary').find('h4').append('<span class="new_title_name">' + title_format[0] + '<b>' + bold_title + '</b>' + '</span>');
            })
            
            clearInterval(checkExist);
        }
    }, 100);

    // =============================================================================
    // OUT OF STOCK SWATCH HANDLING
    // =============================================================================

    // Update this array with the value IDs that are currently out of stock
    const oosValueIds = [
        /*'v_18919696', // CIRCUIT BLACK*/
    ];

    function initOosSwatches() {
        oosValueIds.forEach(id => {
            const el = document.querySelector(`.mw-option__value[data-value-id="${id}"]`);
            if (!el) return;

            el.classList.add('oos-item');

            // Uncheck if somehow pre-selected
            const input = el.querySelector('input');
            if (input && input.checked) {
                input.checked = false;
                el.classList.remove('mw-option__value--selected', 'radio_active');
            }
        });
    }

    // =============================================================================
    // OOS TIPPY TOOLTIP INJECTION
    // =============================================================================

    function checkAndInjectOosTippy(tippyRoot) {
        if (!tippyRoot) return;
        if (tippyRoot.querySelector('.oos-tippy-notify')) return;

        let matchedId = null;
        oosValueIds.forEach(id => {
            const swatch = document.querySelector(
                `.mw-option__value[data-value-id="${id}"] .mw-option__swatch-media`
            );
            if (swatch && swatch._tippy && swatch._tippy.popper === tippyRoot) {
                matchedId = id;
            }
        });

        if (!matchedId) return;

        const content = tippyRoot.querySelector('.tippy-content');
        if (!content) return;

        // Grab the title_name from the matched swatch's parent value element
        const valueEl = document.querySelector(`.mw-option__value[data-value-id="${matchedId}"]`);
        const titleName = valueEl ? valueEl.querySelector('.title_name')?.textContent?.trim() : '';

        const notifyEl = document.createElement('div');
        notifyEl.className = 'oos-tippy-notify';
        notifyEl.innerHTML = `
            ${titleName ? `<span class="oos-tippy-title">${titleName}</span>` : ''}
            <span class="oos-tippy-label">Out of Stock</span>
            <button class="oos-tippy-btn">Notify Me When Available</button>
        `;
        content.appendChild(notifyEl);

        notifyEl.querySelector('.oos-tippy-btn').addEventListener('click', function (e) {
            e.preventDefault();
            e.stopImmediatePropagation();

            const swatch = document.querySelector(
                `.mw-option__value[data-value-id="${matchedId}"] .mw-option__swatch-media`
            );
            if (swatch && swatch._tippy) swatch._tippy.hide();

            const swatchImg = swatch ? swatch.querySelector('img') : null;
            openOosPopup(swatchImg ? swatchImg.src : '');
        });
    }

    // Watch for Tippy tooltips appearing in the DOM.
    // Scoped to document.body for childList (Tippy roots are appended to body directly)
    // but with attributeFilter so attribute mutations only fire for data-state changes.
    // Disconnected after APO fully loads — all OOS swatches are known at that point
    // and no new ones will appear, so continuous observation is wasteful and
    // competes with the WebGL render loop on the main thread.
    const tippyObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
            // Watch for new tippy roots added to body
            mutation.addedNodes.forEach(function (node) {
                if (
                    node.nodeType === 1 &&
                    node.hasAttribute('data-tippy-root')
                ) {
                    setTimeout(() => checkAndInjectOosTippy(node), 50);
                }
            });
            // Also watch for existing tooltips becoming visible
            if (
                mutation.type === 'attributes' &&
                mutation.attributeName === 'data-state'
            ) {
                const box = mutation.target;
                if (
                    box.classList.contains('tippy-box') &&
                    box.getAttribute('data-state') === 'visible'
                ) {
                    const root = box.closest('[data-tippy-root]');
                    setTimeout(() => checkAndInjectOosTippy(root), 50);
                }
            }
        });
    });

    // Only watch body for childList (Tippy appends roots directly to body).
    // Do NOT use subtree:true — that fires on every DOM change in the entire page
    // including canvas repaints, which blocks the WebGL render loop.
    tippyObserver.observe(document.body, {
        childList: true,
        subtree: false,
        attributes: true,
        attributeFilter: ['data-state']
    });

    // Disconnect once APO options are fully ready — swatches won't change after this
    window.addEventListener('optionsReady', function () {
        setTimeout(function () { tippyObserver.disconnect(); }, 2000);
    }, { once: true });

    // Poll until APO swatches are rendered
    const oosCheckInterval = setInterval(function () {
        if (document.querySelector('.mw-option__value[data-value-id]')) {
            initOosSwatches();
            clearInterval(oosCheckInterval);
        }
    }, 300);

    // Also catch the optionsReady event in case polling fires too early
    window.addEventListener('optionsReady', function () {
        setTimeout(initOosSwatches, 800);
    });

    // Open popup - triggers Klaviyo form separately
    function openOosPopup(imgSrc) {
        const popup = document.getElementById('oos-popup');
        const overlay = document.getElementById('oos-overlay');
        if (!popup || !overlay) return;

        const fullSrc = imgSrc ? imgSrc.replace(/\?.*$/, '') : '';
        document.getElementById('oos-popup-img').src = fullSrc;

        overlay.classList.add('oos-visible');
        popup.classList.add('oos-visible');
    }

    function closeOosPopup() {
        document.getElementById('oos-popup')?.classList.remove('oos-visible');
        document.getElementById('oos-overlay')?.classList.remove('oos-visible');
    }

    // Click OOS swatch -> show image preview + trigger Klaviyo form
    document.addEventListener('click', function (e) {
        const swatch = e.target.closest('.oos-item .mw-option__swatch-media');
        if (swatch) {
            e.preventDefault();
            e.stopImmediatePropagation();
            const img = swatch.querySelector('img');
            openOosPopup(img ? img.src : '');
            return;
        }

        // Close on overlay or close button
        if (e.target.id === 'oos-overlay' || e.target.id === 'oos-popup-close') {
            closeOosPopup();
        }
    }, true);

    // ESC key closes popup
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeOosPopup();
    });

    setTimeout(function() {
        function showhide(et) {
            var show_id = $(et).attr('data-id');
            $('mw-option--type-swatch').hide();
            $('#' + show_id).show();
        }
    }, 2000);

    $('.custom-product').show();
});

// =============================================================================
// WINDOW LOAD EVENT - DOPPLE CONTROLS
// =============================================================================

window.addEventListener('load', async () => {

    // Header hide/show logic for desktop
    if (window.innerWidth > 992) {
        $('.header').slideUp(0);
    }

    let isHeaderHovered = false;

    if (window.innerWidth > 992) {
        $('.header').hover(
            function() {
                isHeaderHovered = true;
            },
            function() {
                isHeaderHovered = false;
                $(document).on('mousemove', function(e) {
                    if (e.clientY > 30 && !isHeaderHovered) {
                        $('.header').slideUp();
                    }
                });
            }
        );

        $(document).on('mousemove', function(e) {
            if (e.clientY <= 10 || isHeaderHovered) {
                $('.header').slideDown();
            }
        });
    }
    
    setTimeout(() => {
        if ($('#v_18919714')[0] != undefined && $('#v_18919714')[0] != 'undefined') {
            $('#v_18919714')[0].click();
        }
        
        // URL-based preset loading
        if (window.location.search.includes('bitcoin')) {
            $('#v_19580997').click();
        }
        
        if (window.location.search.includes('drlupo')) {
            if ($('#v_18919699').length > 0 && $('.mw-option__value[data-value-id="v_18919699"]').length > 0) {
                $('#v_18929376')[0].click();
                $('#v_18919699')[0].click();
                $('.mw-option__value[data-value-id="v_17479846"]').css('display', 'block');
                
                var discountCode = "8JN8WESM";
                var urlParams = new URLSearchParams(window.location.search);
                
                if (!urlParams.has('discount')) {
                    urlParams.append('discount', discountCode);
                    var newUrl = `${window.location.pathname}?${urlParams.toString()}`;
                    window.history.replaceState(null, null, newUrl);
                }
            }
        } else {
            $('[data-value-id="v_18142709"]').hide();
        }
        
        if (window.location.search.includes('miami_heretics') || window.location.search.includes('discount=HERETICS200') || window.location.search.includes('heretics=true')) {
            setTimeout(function () {
                $('#v_18929376').click();
                $('body').addClass('darkmode');
                $('#v_20110214').click();
            }, 500);
        }
        
        if (window.location.search.includes('onyx-dunePort-walnut') || window.location.search.includes('onyx-dunePort-walnut=true')) {
            setTimeout(function () {
                $('#v_18919692').click();
                $('#v_18919695').click();
            }, 500);
        }
        
        if (window.location.search.includes('quartz-ivoryPort-circuitWhite') || window.location.search.includes('quartz-ivoryPort-circuitWhite=true')) {
            setTimeout(function () {
                $('#v_18919682').click();
                $('#v_18919689').click();
                $('#v_18919698').click();
            }, 500);
        }
        
        if (window.location.search.includes('onyx-ivoryAthlon-walnut') || window.location.search.includes('onyx-ivoryAthlon-walnut=true')) {
            setTimeout(function () {
                $('#v_18919695').click();
                $('#v_18919683').click();
            }, 500);
        }
        
        if (window.location.search.includes('quartz-lavenderAthlon-circuitBlack') || window.location.search.includes('quartz-lavenderAthlon-circuitBlack=true')) {
            setTimeout(function () {
                $('#v_18919682').click();
                $('#v_18919688').click();
                $('#v_18919697').click();
            }, 500);
        }
        
        if (window.location.search.includes('bitcoin-onyx-ivoryAthlon-cream') || window.location.search.includes('bitcoin-onyx-ivoryAthlon-cream=true')) {
            setTimeout(function () {
                $('#v_18919683').click();
                $('#v_19580996').click();
            }, 500);
        }
        
        if (window.location.search.includes('quartz-onyxPort-naturalOak') || window.location.search.includes('quartz-onyxPort-naturalOak=true')) {
            setTimeout(function () {
                $('#v_18919682').click();
                $('#v_18919690').click();
                $('#v_18919694').click();
            }, 500);
        }
        
        if (window.location.search.includes('onyx-lavenderAthlon-circuitBlack') || window.location.search.includes('onyx-lavenderAthlon-circuitBlack=true')) {
            setTimeout(function () {
                $('#v_18919690').click();
                $('#v_18919697').click();
            }, 500);
        }
        
        if (window.location.search.includes('quartz-steelAthlon-standardOnyx') || window.location.search.includes('quartz-steelAthlon-standardOnyx=true')) {
            setTimeout(function () {
                $('#v_18919682').click();
                $('#v_18919685').click();
            }, 500);
        }
        
        if (window.location.search.includes('AMERICA') || window.location.search.includes('AMERICA=true')) {
            setTimeout(function () {
                $('#v_18919683').click();
                $('#v_18919682').click();
                $('#v_18919704').click();
                $('#v_20616603').click();
            }, 500);
        }
        
        if (window.innerWidth >= 900) {
            setAutoRotation(true);
        }
        
        $(".sticky_img").click(function() {
            setAutoRotation(false);
        });
        
        // =============================================================================
        // PRESET CLICK HANDLERS
        // =============================================================================
        
        $('#v_18929218').click(function() {
            setTimeout(function() {
                clickUntilSelected('#v_18919681');
                clickUntilSelected('#v_18919684');
                clickUntilSelected('#v_18919695');
            }, 100);
        });

        $('#v_18929219').click(function() {
            setTimeout(function() {
                clickUntilSelected('#v_18919682');
                clickUntilSelected('#v_18919683');
                clickUntilSelected('#v_18919694');
            }, 100);
        });

        $('#v_18929373').click(function() {
            setTimeout(function() {
                clickUntilSelected('#v_18919681');
                clickUntilSelected('#v_18919684');
                clickUntilSelected('#v_18919697');
            }, 100);
        });

        $('#v_18929374').click(function() {
            setTimeout(function() {
                clickUntilSelected('#v_18919682');
                clickUntilSelected('#v_18919685');
                clickUntilSelected('#v_18919695');
            }, 100);
        });

        $('#v_19657504').click(function() {
            setTimeout(function() {
                clickUntilSelected('#v_18919682');
                clickUntilSelected('#v_18919683');
                clickUntilSelected('#v_18919698');
            }, 100);
        });

        $('#v_19657503').click(function() {
            setTimeout(function() {
                clickUntilSelected('#v_18919681');
                clickUntilSelected('#v_18919690');
                clickUntilSelected('#v_18919696');
            }, 100);
        });

        $('#v_19657502').click(function() {
            setTimeout(function() {
                clickUntilSelected('#v_18919682');
                clickUntilSelected('#v_18919688');
                clickUntilSelected('#v_18919698');
            }, 100);
        });

        $('#v_17990463').click(function() {
            setTimeout(function() {
                $('#v_18142710')[0].click();
                $('[data-value-id="v_18142709"]').hide();
                $('[data-value-id="v_18142710"]').show(); 
            }, 100);
        });

        $('#v_17990464').click(function() {
            setTimeout(function() {
                $('#v_18142709')[0].click();
                $('[data-value-id="v_18142710"]').hide();
                $('[data-value-id="v_18142709"]').show(); 
            }, 100);
        })
        
        // =============================================================================
        // CHAIR COLOR HANDLERS WITH CAMERA SWITCHING
        // =============================================================================
        
        $('#v_18919681').click(function() {
            $('#v_18919705')[0].click();
            setTimeout(function() {
                setCameraView('back');
            }, 100);
        });

        $('#v_18919682').click(function() {
            $('#v_18919704')[0].click();
            setTimeout(function() {
                setCameraView('back');
            }, 100);
        });

        $('#v_20420817').click(function() {
            $('#v_20420844')[0].click();
            setTimeout(function() {
                setCameraView('back');
            }, 100);
        });

        $('#v_20420818').click(function() {
            $('#v_20420843')[0].click();
            setTimeout(function() {
                setCameraView('back');
            }, 100);
        });
        
        // =============================================================================
        // CAMERA VIEW SECTION HANDLERS - Event Delegation
        // =============================================================================

        // Back panel section -> back view
        $(document).on('click', '.o_2184552 input[type="radio"]', function() {
            setAutoRotation(false);
            setCameraView('back');
        });

        // Fabric section -> front view
        $(document).on('click', '.o_2184551 input[type="radio"]', function() {
            setAutoRotation(false);
            setCameraView('front');
        });

        // Control caps -> back view
        $(document).on('click', '[data-id="o_2081591"] input[type="radio"]', function() {
            setAutoRotation(false);
            setCameraView('back');
        });

        // Base section -> front view
        $(document).on('click', '[data-id="o_2081595"] input[type="radio"]', function() {
            setAutoRotation(false);
            setCameraView('front');
        });

        // Adjustment section -> lowbackAdjust view
        $(document).on('click', '.o_2184553 input[type="radio"]', function() {
            setAutoRotation(false);
            setCameraView('lowbackAdjust');
        });
    }, 200);
});

// =============================================================================
// AR UI HANDLERS
// =============================================================================

$('#nativeUIIconButton-av-ar').click(function() {
    $('.sticky_img').css({
        'z-index': '10000',
    });
});

$('.native-ui__modal-close-button').click(function() {
    $('.sticky_img').css({
        'z-index': '9',
    });
});

// =============================================================================
// RESPONSIVE HEIGHT ADJUSTMENT
// =============================================================================

$(document).ready(function() {
    if ($(window).width() > 992) {
        var height = $(window).height();
        $('.custom_product_section.product__info-wrapper').attr('style', 'max-height:' + height + 'px; overflow:scroll;');
        
        window.addEventListener('resize', function(event) {
            height = $(window).height();
            $('.custom_product_section.product__info-wrapper').attr('style', 'max-height:' + height + 'px; overflow:scroll;');
        });
    }
    
    // =============================================================================
    // MOBILE SCROLL BEHAVIOR
    // =============================================================================
    
    checkScroll();

    var _st3 = 0;
    $(window).scroll(function() {
        var _n = Date.now(); if (_n - _st3 < 100) return; _st3 = _n;
        checkScroll();
    });

    function checkScroll() {
        if ($(window).width() < 992) {
            var scrollTop = $(window).scrollTop();
            var maxScroll = 150;
            
            if (scrollTop > 0) {
                $('.mobile_price').addClass('price_fixed');
                var scrollProgress = Math.min(scrollTop / maxScroll, 1);
                
                $('.review_img, .affirm-line, .mobileBlkFriday, .black_friday.mobile_display').each(function() {
                    var originalHeight = $(this).data('originalHeight') || $(this).outerHeight();
                    $(this).data('originalHeight', originalHeight);
                    
                    var originalMarginTop = $(this).data('originalMarginTop');
                    if (originalMarginTop === undefined) {
                        originalMarginTop = parseFloat($(this).css('margin-top')) || 0;
                        $(this).data('originalMarginTop', originalMarginTop);
                    }
                    
                    var currentMarginTop = originalMarginTop - (10 * scrollProgress);
                    
                    $(this).css({
                        'max-height': (originalHeight * (1 - scrollProgress)) + 'px',
                        'opacity': 1 - scrollProgress,
                        'overflow': 'hidden',
                        'margin-top': $(this).hasClass('affirm-line') ? '' : currentMarginTop + 'px',
                        'margin-bottom': ($(this).data('originalMarginBottom') || parseFloat($(this).css('margin-bottom'))) * (1 - scrollProgress) + 'px',
                        'padding-top': ($(this).data('originalPaddingTop') || parseFloat($(this).css('padding-top'))) * (1 - scrollProgress) + 'px',
                        'padding-bottom': ($(this).data('originalPaddingBottom') || parseFloat($(this).css('padding-bottom'))) * (1 - scrollProgress) + 'px',
                        'transition': 'none'
                    });
                    
                    if (!$(this).data('originalMarginBottom')) {
                        $(this).data('originalMarginBottom', parseFloat($(this).css('margin-bottom')) || 0);
                        $(this).data('originalPaddingTop', parseFloat($(this).css('padding-top')) || 0);
                        $(this).data('originalPaddingBottom', parseFloat($(this).css('padding-bottom')) || 0);
                    }
                });
                
            } else {
                $('.mobile_price').removeClass('price_fixed');
                
                $('.review_img, .affirm-line, .mobileBlkFriday, .black_friday.mobile_display').css({
                    'max-height': 'none',
                    'opacity': 1,
                    'margin-top': '',
                    'margin-bottom': '',
                    'padding-top': '',
                    'padding-bottom': '',
                    'transition': 'max-height 0.3s ease, opacity 0.3s ease, margin 0.3s ease, padding 0.3s ease'
                });
            }
        }
    }
    
    // =============================================================================
    // OPTION ERROR DISPLAY
    // =============================================================================
    
    // Was polled every 100ms (10×/sec forever, including during Dopple's
    // 60fps WebGL render). 500ms is still plenty responsive for surfacing
    // option validation errors and cuts main-thread wake-ups by 5×.
    setInterval(function() {
        $('.option_error').remove();
        var error_html = '';

        $('.mw-stack__item .mw-option__helptext').each(function() {
            if (!$(this).hasClass('mw-hidden')) {
                error_html += '<p>' + $(this).text() + '</p>';
            }
        })

        if (error_html) {
            $('<div id="option_error" class="option_error">' + error_html + '</div>').insertBefore('.affirm-line');
        }
    }, 500);
});
// ─── Holiday Sale Promo Banner Injections ───────────────────────────────────
const MEMORIAL_BANNERS = [
  {
    selector: '.chair_summary.o_2184550',
    img: 'https://cdn.shopify.com/s/files/1/0611/7552/9642/files/Config_-_Quartz_Upgrade_Section_Header.png?v=1778603735',
    alt: 'Save $99 with Memorial Day Deal - Free Quartz Color Upgrade'
  },
  {
    selector: '.chair_summary.o_2184552',
    img: 'https://cdn.shopify.com/s/files/1/0611/7552/9642/files/Config_-_Back_Upgrade_Section_Header.png?v=1778603735',
    alt: 'Save up to $299 with Memorial Day Deal - Free Back Panel Upgrade'
  },
  {
    selector: '.chair_summary.o_2184551',
    img: 'https://cdn.shopify.com/s/files/1/0611/7552/9642/files/Config_-_Cover_Upgrade_Section_Header.png?v=1778603735',
    alt: 'Save $199 with Memorial Day Deal - Free Cover Upgrade'
  },
  {
    selector: '.chair_summary.o_2184553',
    img: 'https://cdn.shopify.com/s/files/1/0611/7552/9642/files/Config_-_Knob_Cap_Upgrade_Section_Header.png?v=1778603735',
    alt: 'Save up to $49 with Memorial Day Deal - Free Knob Cap Upgrade'
  }
];

function injectMemorialBanners() {
  let allInjected = true;

  MEMORIAL_BANNERS.forEach(({ selector, img, alt }) => {
    const section = document.querySelector(selector);
    if (!section) { allInjected = false; return; }
    if (section.querySelector('.memorial-promo-banner')) return;

    let insertAfter = null;

    const subtxt = section.querySelector('.subtxt');
    if (subtxt) {
      insertAfter = subtxt.closest('.mw-stack--spacing-extra-tight').closest('.mw-stack__item');
    }

    if (!insertAfter) {
      const mwOption = section.querySelector('.mw-option');
      if (mwOption) {
        const topStack = mwOption.querySelector('.mw-stack--vertical');
        if (topStack) {
          insertAfter = topStack.querySelector(':scope > .mw-stack__item');
        }
      }
    }

    if (!subtxt) {
      const emptyLabelBlock = section.querySelector('.mw-stack--spacing-extra-tight');
      if (emptyLabelBlock) {
        const wrapper = emptyLabelBlock.closest('.mw-stack__item');
        if (wrapper) wrapper.style.cssText = 'display:none;';
      }
    }

    if (!insertAfter) { allInjected = false; return; }

    const banner = document.createElement('img');
    banner.src = img;
    banner.alt = alt;
    banner.className = 'memorial-promo-banner';
    banner.style.cssText = 'width:100%;display:block;margin-bottom:12px;';
    insertAfter.insertAdjacentElement('afterend', banner);
  });

  return allInjected;
}

function getActualPrice() {
    const base = parseFloat(document.getElementById('product_price')?.value || 2100);
    let addons = 0;
    document.querySelectorAll('.mw-apo-container input[type="radio"]:checked').forEach(input => {
        addons += parseInt(input.getAttribute('data-price') || 0, 10);
    });
    return base + addons;
}

function initMemorialStrikethrough() {
    updateMemorialStrikethrough();

    document.querySelector('.mw-apo-container')?.addEventListener('change', () => {
        setTimeout(updateMemorialStrikethrough, 200);
    });
}

if (MEMORIAL_DAY_ACTIVE) {
    const salePollInterval = setInterval(() => {
        const ready = document.querySelector('.chair_summary.o_2184550');
        if (ready) {
            injectMemorialSavingsLines();
            initMemorialStrikethrough();
            clearInterval(salePollInterval);
        }
    }, 300);

    setTimeout(() => clearInterval(salePollInterval), 15000);
}

// ─── Memorial Day Countdown ─────────────────────────────────────────────────

function initMemorialCountdown() {
    const deadline = new Date(SALE_DEADLINE).getTime();

    const second = 1000;
    const minute = second * 60;
    const hour = minute * 60;
    const day = hour * 24;
    const pad = function (n) { return (n < 10 ? '0' : '') + n; };

    const countdownIds = [
        { days: 'days',  hours: 'hours',  minutes: 'minutes',  seconds: 'seconds',  wrap: 'countdown' },
        { days: 'days2', hours: 'hours2', minutes: 'minutes2', seconds: 'seconds2', wrap: 'countdown2' }
    ];

    const x = setInterval(function() {
        const distance = deadline - new Date().getTime();

        if (distance < 0) {
            clearInterval(x);
            countdownIds.forEach(function(ids) {
                const wrap = document.getElementById(ids.wrap);
                if (wrap) wrap.style.display = 'none';
            });
            document.querySelectorAll('.memorial-promo-banner').forEach(function(el) {
                el.style.display = 'none';
            });
            return;
        }

        const d = Math.floor(distance / day);
        const h = Math.floor((distance % day) / hour);
        const m = Math.floor((distance % hour) / minute);
        const s = Math.floor((distance % minute) / second);

        countdownIds.forEach(function(ids) {
            const dEl = document.getElementById(ids.days);
            const hEl = document.getElementById(ids.hours);
            const mEl = document.getElementById(ids.minutes);
            const sEl = document.getElementById(ids.seconds);
            if (dEl) dEl.innerText = pad(d);
            if (hEl) hEl.innerText = pad(h);
            if (mEl) mEl.innerText = pad(m);
            if (sEl) sEl.innerText = pad(s);
        });

    }, 1000);
}

if (MEMORIAL_DAY_ACTIVE) {
    document.addEventListener('DOMContentLoaded', initMemorialCountdown);
}